import { createTRPCClient, httpLink } from '@trpc/client'
import { createTRPCReact } from '@trpc/react-query'
import SuperJSON from 'superjson'
import { API_URL } from './constants'

// ── Model catalog sync + probe (mirrors apps/api/src/services/ai) ──
export interface CatalogCandidate {
  id: string
  author: string
  name: string
  created: number | null
  contextLength: number | null
  pricing: { input: number, output: number } | null
  inputModalities: string[]
  outputModalities: string[]
  isDated: boolean
  isExpiring: boolean
  inCurrentConfig: boolean
  suggested: boolean
  missingFromOpenRouter: boolean
}

export interface ModelCatalogDiff {
  fetchedAt: string
  authors: string[]
  totalCatalogSize: number
  byAuthor: Record<string, CatalogCandidate[]>
  toAdd: string[]
  toRetire: string[]
  kept: string[]
  suggestedConfig: Record<string, string[]>
  currentConfig: Record<string, string[]>
}

export interface ProbeResult {
  ok: boolean
  latencyMs: number
  output?: string
  tokensOut?: number
  error?: string
}

export interface ModelProbeReport extends ProbeResult {
  model: string
  provider: 'official' | 'openrouter'
}

// Define comprehensive router types based on the actual API structure
type DataRouter = {
  getModelOverallWinningRate: {
    query: (input?: { gameSetting?: string }) => Promise<{
      success: true
      data: unknown
      lastUpdated: string
    }>
  }
  getWinningRatePerModelPerRole: {
    query: () => Promise<{
      success: true
      data: unknown
      lastUpdated: string
    }>
  }
  getModelActionPreciseRate: {
    query: (input?: { gameSetting?: string }) => Promise<{
      success: true
      data: unknown
      lastUpdated: string
    }>
  }
  getAllGameStats: {
    query: () => Promise<{
      success: true
      data: Record<string, GameDataStructure>
      lastUpdated: string
    }>
  }
  refreshAllCache: {
    mutate: () => Promise<{
      success: true
      message: string
      data: Record<string, GameDataStructure>
      timestamp: string
    }>
  }
  refreshResponsiveModelsCache: {
    mutate: () => Promise<{
      success: true
      message: string
    }>
  }
  getGameDataById: {
    query: (input: { gameId: string }) => Promise<GameDataResponse>
  }
  getGamesByFilters: {
    query: (input: {
      languages?: string[]
      statuses?: string[]
      models?: string[]
      userId?: string
      gameId?: string
      tags?: Array<'game' | 'data'>
    }) => Promise<GamesByFiltersResponse>
  }
  getFilterOptions: {
    query: () => Promise<FilterOptionsResponse>
  }
  getRecentGames: {
    query: (input: {
      page: number
      limit: number
    }) => Promise<GamesByFiltersResponse>
  }
  getGameSettingTypes: {
    query: () => Promise<{
      success: true
      data: string[]
      message: string
    }>
  }
  getResponsiveModels: {
    query: () => Promise<{
      success: true
      data: {
        lowCost: string[]
        highPerformance: string[]
      }
      message: string
    }>
  }
  getModelConfig: {
    query: () => Promise<{
      success: true
      data: Record<string, string[]>
      message: string
    }>
  }
  getGameModelPools: {
    query: () => Promise<{
      success: true
      data: { lowCost: Record<string, string[]>, highPerformance: Record<string, string[]> }
      message: string
    }>
  }
  setGameModelPools: {
    mutate: (input: {
      lowCost?: Record<string, string[]>
      highPerformance?: Record<string, string[]>
    }) => Promise<{ success: true, message: string }>
  }
  getBlockedKeywords: {
    query: () => Promise<{
      success: boolean
      data: { name: string[], email: string[], displayName: string[] }
      message: string
    }>
  }
  addBlockedKeyword: {
    mutate: (input: {
      keyword: string
      field: 'name' | 'email' | 'displayName'
    }) => Promise<{ success: boolean, message: string }>
  }
  removeBlockedKeyword: {
    mutate: (input: {
      keyword: string
      field: 'name' | 'email' | 'displayName'
    }) => Promise<{ success: boolean, message: string }>
  }
  getWhitelistEmails: {
    query: () => Promise<{
      success: boolean
      data: string[]
      message: string
    }>
  }
  addWhitelistEmail: {
    mutate: (input: { email: string }) => Promise<{ success: boolean, message: string }>
  }
  removeWhitelistEmail: {
    mutate: (input: { email: string }) => Promise<{ success: boolean, message: string }>
  }
  syncModelPricing: {
    mutate: () => Promise<{
      success: boolean
      data: Record<string, { input: number, output: number, source: 'direct' | 'openrouter' }>
      providerSources: Record<string, 'direct' | 'openrouter'>
      message: string
    }>
  }
  getModelPricing: {
    query: () => Promise<{
      success: boolean
      data: Record<string, { input: number, output: number, source: 'direct' | 'openrouter' }>
      providerSources: Record<string, 'direct' | 'openrouter'>
      message: string
    }>
  }
  fetchModelCatalogDiff: {
    mutate: () => Promise<{
      success: boolean
      data: ModelCatalogDiff
      message: string
    }>
  }
  probeModels: {
    mutate: (input: { models: string[], timeoutMs?: number }) => Promise<{
      success: boolean
      data: ModelProbeReport[]
      message: string
    }>
  }
  applyModelConfigUpdate: {
    mutate: (input: { modelConfig: Record<string, string[]>, refreshResponsive?: boolean }) => Promise<{
      success: boolean
      data: {
        modelCount: number
        added: string[]
        removed: string[]
        poolsPruned: number
        responsiveRefreshed: boolean
      }
      message: string
    }>
  }
  getReconcileStatus: {
    query: () => Promise<{
      success: boolean
      drifted: Array<{
        id: string
        email: string
        cachedBank: number
        ledgerTotal: number
        drift: number
      }>
      totalUsers: number
    }>
  }
  reconcileUser: {
    mutate: (input: { userId: string }) => Promise<{
      success: boolean
      userId: string
      newBank: number
    }>
  }
}

type BatchRouter = {
  health: {
    query: () => Promise<{ status: string, service: string }>
  }
  stimulateGamesBatch: {
    mutate: (input: {
      gameCount: number
      languagePercentages: Record<string, number>
      gameSetting: string
      goodModels: string[]
      evilModels: string[]
    }) => Promise<StimulateGamesBatchResponse>
  }
  sendMarketingEmailBatch: {
    mutate: (input: {
      subject: string
      htmlBody: string
      testEmail?: string
      specificEmails?: string[]
      targetLanguage?: 'zh' | 'non-zh'
    }) => Promise<{
      success: boolean
      message: string
      count: number
      details?: { sent: number, failed: number }
    }>
  }
  updateUserLanguages: {
    mutate: () => Promise<{
      success: boolean
      message: string
      details: {
        totalUsers: number
        languageBreakdown: Record<string, number>
      }
    }>
  }
}

type GameRouter = {
  create: {
    mutate: (input: unknown) => Promise<unknown>
  }
  continue: {
    mutate: (input: unknown) => Promise<unknown>
  }
}

type HealthRouter = {
  status: {
    query: () => Promise<unknown>
  }
}

type StrictAppRouter = {
  queryRouter: DataRouter
  batchRouter: BatchRouter
  gameRouter: GameRouter
  healthRouter: HealthRouter
}

// Define response types
export type GameDataStructure = Record<string, unknown>

export type GameDataResponse
  = | {
    success: true
    data: {
      game: {
        id: string
        status: string
        gameSetting: string
        gameType: string
        goodModel: string
        evilModel: string
        winner: string | null
        data: GameDataStructure | null
        godLog: string
        systemPrompt: string
        createdAt: Date | string
        updatedAt: Date | string
      } | null
      players: Array<{
        id: string
        name: string
        position: number
        role: string
        faction: string
        modelName: string
        status: string
        log: string
        createdAt: Date | string
        updatedAt: Date | string
      }>
      actions: Array<{
        id: string
        playerId: string
        playerName: string
        playerRole: string
        roundId: string
        actionType: string
        input: string
        output: string
        createdAt: Date | string
        updatedAt: Date | string
        round: {
          roundNumber: number
          phase: string
          subPhase: string
        }
        roundNumber: number
        phase: string
        subPhase: string
      }>
    }
  }
  | {
    success: false
    error: string
    data: {
      game: null
      players: []
      actions: []
    }
  }

export type StimulateGamesBatchResponse = {
  success: true
  gameIds: string[]
  gameCount: number
  combinations: number
  languageStats: Record<string, { gamesCreated: number, gameIds: string[] }>
  message: string
}
export type GamesByFiltersResponse
  = | {
    success: true
    data: Array<{
      id: string
      status: string
      gameSetting: string
      gameType: string
      goodModel: string
      evilModel: string
      winner: string | null
      data: GameDataStructure | null
      createdAt: Date | string
      updatedAt: Date | string
    }>
    pagination?: {
      page: number
      limit: number
      totalCount: number
      totalPages: number
      hasNext: boolean
      hasPrev: boolean
    }
  }
  | {
    success: false
    error: string
    data: []
    pagination?: {
      page: number
      limit: number
      totalCount: number
      totalPages: number
      hasNext: boolean
      hasPrev: boolean
    }
  }

export type FilterOptionsResponse
  = | {
    success: true
    data: {
      languages: string[]
      statuses: string[]
      models: string[]
    }
  }
  | {
    success: false
    error: string
    data: {
      languages: []
      statuses: []
      models: []
    }
  }

export type ModelPairStats = {
  model1: string
  model2: string
  model1AsGood: number
  model1AsEvil: number
  model2AsGood: number
  model2AsEvil: number
}

export type TotalGameStatsResponse
  = | {
    success: true
    data: ModelPairStats[]
    lastUpdated: string
  }
  | {
    success: false
    error: string
    data: []
    lastUpdated: string
  }

// Create the standalone client for direct API calls with strict typing
export const trpcClient = createTRPCClient({
  links: [
    httpLink({
      url: `${API_URL}/api`,
      transformer: SuperJSON,
    }),
  ],
}) as unknown as StrictAppRouter

// Define React hooks types for strict typing
type ReactDataRouter = {
  getModelOverallWinningRate: {
    useQuery: (
      input?: { gameSetting?: string },
      options?: unknown
    ) => {
      data: unknown
      isLoading: boolean
      error: unknown
    }
  }
  getWinningRatePerModelPerRole: {
    useQuery: (
      input?: unknown,
      options?: unknown
    ) => {
      data: unknown
      isLoading: boolean
      error: unknown
    }
  }
  getModelActionPreciseRate: {
    useQuery: (
      input?: { gameSetting?: string },
      options?: unknown
    ) => {
      data: unknown
      isLoading: boolean
      error: unknown
    }
  }
  getAllGameStats: {
    useQuery: (
      input?: Record<string, never>,
      options?: unknown
    ) => {
      data:
        | {
          success: true
          data: Record<string, GameDataStructure>
          lastUpdated: string
        }
        | undefined
      isLoading: boolean
      error: unknown
      refetch: () => Promise<void>
    }
  }
  getGameDataById: {
    useQuery: (
      input: { gameId: string },
      options?: unknown
    ) => {
      data: GameDataResponse | undefined
      isLoading: boolean
      error: unknown
    }
  }
  getGamesByFilters: {
    useQuery: (
      input: {
        languages?: string[]
        statuses?: string[]
        models?: string[]
        userId?: string
        gameId?: string
        tags?: Array<'game' | 'data'>
      },
      options?: unknown
    ) => {
      data: GamesByFiltersResponse | undefined
      isLoading: boolean
      error: unknown
    }
  }
  getFilterOptions: {
    useQuery: (
      input?: unknown,
      options?: unknown
    ) => {
      data: FilterOptionsResponse | undefined
      isLoading: boolean
      error: unknown
    }
  }
  getRecentGames: {
    useQuery: (
      input: { page: number, limit: number },
      options?: unknown
    ) => {
      data: GamesByFiltersResponse | undefined
      isLoading: boolean
      error: unknown
    }
  }
  getGameSettingTypes: {
    useQuery: (
      input?: unknown,
      options?: unknown
    ) => {
      data:
        | {
          success: true
          data: string[]
          message: string
        }
        | undefined
      isLoading: boolean
      error: unknown
    }
  }
  getResponsiveModels: {
    useQuery: (
      input?: unknown,
      options?: unknown
    ) => {
      data:
        | {
          success: true
          data: {
            lowCost: string[]
            highPerformance: string[]
          }
          message: string
        }
        | undefined
      isLoading: boolean
      error: unknown
    }
  }
  getModelConfig: {
    useQuery: (
      input?: unknown,
      options?: unknown
    ) => {
      data:
        | {
          success: true
          data: Record<string, string[]>
          message: string
        }
        | undefined
      isLoading: boolean
      error: unknown
    }
  }
  getGameModelPools: {
    useQuery: (
      input?: unknown,
      options?: unknown
    ) => {
      data:
        | {
          success: true
          data: { lowCost: Record<string, string[]>, highPerformance: Record<string, string[]> }
          message: string
        }
        | undefined
      isLoading: boolean
      error: unknown
    }
  }
  setGameModelPools: {
    useMutation: (options?: unknown) => {
      mutate: (input: {
        lowCost?: Record<string, string[]>
        highPerformance?: Record<string, string[]>
      }) => Promise<{ success: true, message: string }>
      isLoading: boolean
      error: unknown
    }
  }
  getBlockedKeywords: {
    useQuery: (
      input?: unknown,
      options?: unknown
    ) => {
      data:
        | {
          success: boolean
          data: { name: string[], email: string[], displayName: string[] }
          message: string
        }
        | undefined
      isLoading: boolean
      error: unknown
    }
  }
  addBlockedKeyword: {
    useMutation: (options?: unknown) => {
      mutate: (input: {
        keyword: string
        field: 'name' | 'email' | 'displayName'
      }) => Promise<{ success: boolean, message: string }>
      isLoading: boolean
      error: unknown
    }
  }
  removeBlockedKeyword: {
    useMutation: (options?: unknown) => {
      mutate: (input: {
        keyword: string
        field: 'name' | 'email' | 'displayName'
      }) => Promise<{ success: boolean, message: string }>
      isLoading: boolean
      error: unknown
    }
  }
  refreshAllCache: {
    useMutation: (options?: unknown) => {
      mutate: () => Promise<{
        success: true
        message: string
        data: Record<string, GameDataStructure>
        timestamp: string
      }>
      isLoading: boolean
      error: unknown
    }
  }
  refreshResponsiveModelsCache: {
    useMutation: (options?: unknown) => {
      mutate: () => Promise<{
        success: true
        message: string
      }>
      isLoading: boolean
      error: unknown
    }
  }
}

type ReactBatchRouter = {
  health: {
    useQuery: (
      input?: unknown,
      options?: unknown
    ) => {
      data: unknown
      isLoading: boolean
      error: unknown
    }
  }
  stimulateGamesBatch: {
    useMutation: (options?: unknown) => {
      mutate: (input: {
        gameCount: number
        languagePercentages: Record<string, number>
        gameSetting: string
        goodModels: string[]
        evilModels: string[]
      }) => Promise<StimulateGamesBatchResponse>
      isLoading: boolean
      error: unknown
    }
  }
  sendMarketingEmailBatch: {
    useMutation: (options?: unknown) => {
      mutate: (input: {
        subject: string
        htmlBody: string
        testEmail?: string
        specificEmails?: string[]
        targetLanguage?: 'zh' | 'non-zh'
      }) => Promise<{
        success: boolean
        message: string
        count: number
        details?: { sent: number, failed: number }
      }>
      isLoading: boolean
      error: unknown
    }
  }
  updateUserLanguages: {
    useMutation: (options?: unknown) => {
      mutate: () => Promise<{
        success: boolean
        message: string
        details: {
          totalUsers: number
          languageBreakdown: Record<string, number>
        }
      }>
      isLoading: boolean
      error: unknown
    }
  }
}

type ReactGameRouter = {
  create: {
    useMutation: (options?: unknown) => {
      mutate: (input: unknown) => Promise<unknown>
      isLoading: boolean
      error: unknown
    }
  }
  continue: {
    useMutation: (options?: unknown) => {
      mutate: (input: unknown) => Promise<unknown>
      isLoading: boolean
      error: unknown
    }
  }
}

type ReactHealthRouter = {
  status: {
    useQuery: (
      input?: unknown,
      options?: unknown
    ) => {
      data: unknown
      isLoading: boolean
      error: unknown
    }
  }
}

type StrictReactAppRouter = {
  Provider: React.ComponentType<{
    client: unknown
    queryClient: unknown
    children: React.ReactNode
  }>
  queryRouter: ReactDataRouter
  batchRouter: ReactBatchRouter
  gameRouter: ReactGameRouter
  healthRouter: ReactHealthRouter
}

// Create the React hooks for component usage with strict typing
export const trpc = createTRPCReact() as unknown as StrictReactAppRouter

// Create a proper tRPC client for React hooks with strict typing
export const trpcClientForReact = createTRPCClient({
  links: [
    httpLink({
      url: `${API_URL}/api`,
      transformer: SuperJSON,
    }),
  ],
}) as unknown as StrictAppRouter
